import { t } from '@lingui/core/macro';
import type { BrandInsightsRecord, BrandInsightsTotalsRecord } from '@navio/nsdk';
import { NoddiBadge, NoddiSkeleton } from 'noddi-ui-web';
import type { ReactNode } from 'react';

import { formatChange, formatCount, formatMoney, getRelativeChange } from './brandInsightsFormat';

interface Props {
  /** Undefined while the period loads. */
  insights?: BrandInsightsRecord;
  comparisonLabel: string;
}

const averageOrderValue = (totals: BrandInsightsTotalsRecord) =>
  totals.bookings > 0 ? totals.revenue.amount / totals.bookings : 0;

/** The period's headline figures; the top of the overview card. */
export const BrandInsightsHero = ({ insights, comparisonLabel }: Props) =>
  insights ? <HeroFigures insights={insights} comparisonLabel={comparisonLabel} /> : <HeroSkeleton />;

const HeroFigures = ({ insights, comparisonLabel }: { insights: BrandInsightsRecord; comparisonLabel: string }) => {
  const { totals, previousTotals } = insights;
  const brandName = insights.brand.name;
  const revenueChange = previousTotals ? getRelativeChange(totals.revenue.amount, previousTotals.revenue.amount) : null;
  const bookingsChange = previousTotals ? getRelativeChange(totals.bookings, previousTotals.bookings) : null;
  const aovChange = previousTotals
    ? getRelativeChange(averageOrderValue(totals), averageOrderValue(previousTotals))
    : null;

  const logoUrl = insights.brand.logo.url;

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex flex-col gap-2'>
        <div className='flex items-center gap-3 text-sm'>
          {logoUrl ? (
            // A bordered white tile keeps a logo with its own background from bleeding into the card tint.
            <img
              src={logoUrl}
              alt={brandName}
              className='h-10 w-auto max-w-32 shrink-0 rounded-lg border border-border bg-white object-contain px-2 py-1'
            />
          ) : null}
          <span className='font-semibold'>{t`${brandName} marketplace`}</span>
        </div>
        <span className='text-4xl font-semibold tracking-tight tabular-nums md:text-5xl'>
          {formatMoney(totals.revenue)}
        </span>
        <div className='flex flex-wrap items-center gap-3 text-sm'>
          <span className='text-muted-foreground'>{t`Value of bookings made, incl. VAT`}</span>
          {revenueChange !== null && (
            <NoddiBadge variant={revenueChange >= 0 ? 'success' : 'warning'}>
              {formatChange(revenueChange)} {comparisonLabel}
            </NoddiBadge>
          )}
        </div>
      </div>

      <div className='grid grid-cols-2 gap-5 md:grid-cols-3'>
        <HeroStat
          label={t`Bookings`}
          value={formatCount(totals.bookings)}
          detail={bookingsChange === null ? null : formatChange(bookingsChange)}
        />
        <HeroStat
          label={t`Avg. order value`}
          value={formatMoney({ ...totals.revenue, amount: averageOrderValue(totals) })}
          detail={aovChange === null ? null : formatChange(aovChange)}
        />
        <HeroStat
          label={t`Active service partners`}
          value={formatCount(insights.activePartnerCount)}
          detail={t`in this period`}
        />
      </div>
    </div>
  );
};

// Mirrors HeroFigures line for line so the card keeps its height when the figures arrive.
const HeroSkeleton = () => (
  <div className='flex flex-col gap-6'>
    <div className='flex flex-col gap-2'>
      <div className='flex items-center gap-3'>
        <NoddiSkeleton variant='button' className='h-10 w-20 shrink-0 rounded-lg' />
        <NoddiSkeleton variant='button' className='h-5 w-40' />
      </div>
      <NoddiSkeleton variant='button' className='h-10 w-48 md:h-12 md:w-64' />
      <NoddiSkeleton variant='button' className='h-5 w-60' />
    </div>
    <div className='grid grid-cols-2 gap-5 md:grid-cols-3'>
      {[t`Bookings`, t`Avg. order value`, t`Active service partners`].map((label) => (
        <HeroStat
          key={label}
          label={label}
          value={<NoddiSkeleton variant='button' className='h-8 w-20' />}
          detail={<NoddiSkeleton variant='button' className='h-5 w-16' />}
        />
      ))}
    </div>
  </div>
);

const HeroStat = ({ label, value, detail }: { label: string; value: ReactNode; detail: ReactNode }) => (
  <div className='flex flex-col gap-1 border-l border-border pl-4'>
    <span className='text-sm text-muted-foreground'>{label}</span>
    <span className='text-2xl font-semibold tabular-nums'>{value}</span>
    {detail && <span className='text-sm text-muted-foreground'>{detail}</span>}
  </div>
);
